import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { FONTS } from '../../constants/config';
import { CalendarEvent, EVENT_CATEGORY_COLORS } from './types';

interface MonthlyGridProps {
  currentDate: Date;
  events: CalendarEvent[];
  onEventPress?: (event: CalendarEvent) => void;
}

const AR_DAY_HEADERS = ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'];
const EN_DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MAX_VISIBLE_EVENTS = 3;

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toISODateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Get Monday on or before the first day of the month */
function getGridStart(year: number, month: number): Date {
  const firstDay = new Date(year, month, 1);
  const day = firstDay.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  const d = new Date(firstDay);
  d.setDate(firstDay.getDate() + diff);
  return d;
}

/** Build weeks array for the month grid (5-6 weeks of 7 days) */
function buildMonthWeeks(year: number, month: number): Date[][] {
  const gridStart = getGridStart(year, month);
  const weeks: Date[][] = [];
  let current = new Date(gridStart);

  // Generate at least 5 weeks, up to 6 if the month extends into it
  for (let w = 0; w < 6; w++) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);

    // Stop early if the entire next week would be in the next month
    if (current.getMonth() !== month && current.getDay() === 1) {
      break;
    }
  }

  return weeks;
}

function EventChip({
  event,
  onPress,
}: {
  event: CalendarEvent;
  onPress?: (event: CalendarEvent) => void;
}) {
  const baseConfig = EVENT_CATEGORY_COLORS[event.category] || { bg: '#ff7f0e', border: '#ff7f0e', text: '#FFFFFF', variant: 'solid' as const };
  const colorConfig = event.color
    ? { bg: event.color, border: event.color, text: '#FFFFFF', variant: 'solid' as const }
    : baseConfig;

  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={() => onPress?.(event)}
      style={[styles.chip, { backgroundColor: `${colorConfig.border}18`, borderStartColor: colorConfig.border }]}
    >
      <Text style={[styles.chipText, { color: colorConfig.border }]} numberOfLines={1}>
        {event.title}
      </Text>
    </TouchableOpacity>
  );
}

export default function MonthlyGrid({
  currentDate,
  events,
  onEventPress,
}: MonthlyGridProps) {
  const { colors } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const today = useMemo(() => new Date(), []);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const weeks = useMemo(() => buildMonthWeeks(year, month), [year, month]);

  const dayHeaders = isRTL ? AR_DAY_HEADERS : EN_DAY_HEADERS;

  // Index events by date string for fast lookup
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const event of events) {
      if (!map[event.date]) {
        map[event.date] = [];
      }
      map[event.date].push(event);
    }
    return map;
  }, [events]);

  return (
    <View style={styles.container}>
      {/* Day-of-week header row */}
      <View style={[styles.headerRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        {dayHeaders.map((name) => (
          <View key={name} style={styles.headerCell}>
            <Text style={[styles.headerText, { color: colors.textMuted }]}>
              {name}
            </Text>
          </View>
        ))}
      </View>

      {/* Week rows */}
      {weeks.map((week, weekIndex) => (
        <View
          key={weekIndex}
          style={[
            styles.weekRow,
            { flexDirection: isRTL ? 'row-reverse' : 'row', borderTopColor: colors.borderLight },
          ]}
        >
          {week.map((day) => {
            const isCurrentMonth = day.getMonth() === month;
            const isToday = isSameDay(day, today);
            const dateStr = toISODateString(day);
            const dayEvents = eventsByDate[dateStr] || [];
            const overflowCount = dayEvents.length - MAX_VISIBLE_EVENTS;

            return (
              <View
                key={dateStr}
                style={[
                  styles.dayCell,
                  { borderColor: colors.borderLight },
                  isToday && { backgroundColor: `${'#a9211f'}08` },
                ]}
              >
                {/* Day number */}
                <View style={styles.dayNumberRow}>
                  <View
                    style={[
                      styles.dayNumberWrapper,
                      isToday && { backgroundColor: '#a9211f' },
                    ]}
                  >
                    <Text
                      style={[
                        styles.dayNumber,
                        {
                          color: isToday
                            ? '#FFFFFF'
                            : isCurrentMonth
                              ? colors.text
                              : colors.textLight,
                        },
                      ]}
                    >
                      {day.getDate()}
                    </Text>
                  </View>
                </View>

                {/* Event chips (max 3) */}
                <View style={styles.chipContainer}>
                  {dayEvents.slice(0, MAX_VISIBLE_EVENTS).map((event) => (
                    <EventChip
                      key={event.id}
                      event={event}
                      onPress={onEventPress}
                    />
                  ))}

                  {overflowCount > 0 && (
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => onEventPress?.(dayEvents[MAX_VISIBLE_EVENTS])}
                    >
                      <Text style={[styles.overflowText, { color: colors.textMuted }]}>
                        +{overflowCount} {t('activityCalendar.moreActivities')}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
  headerRow: {
    borderBottomWidth: 0,
  },
  headerCell: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
  },
  headerText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    lineHeight: 16,
  },
  weekRow: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  dayCell: {
    flex: 1,
    minHeight: 100,
    paddingVertical: 4,
    paddingHorizontal: 3,
    borderWidth: 0,
  },
  dayNumberRow: {
    alignItems: 'center',
    marginBottom: 2,
  },
  dayNumberWrapper: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayNumber: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    lineHeight: 16,
  },
  chipContainer: {
    gap: 2,
  },
  chip: {
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderStartWidth: 3,
  },
  chipText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    lineHeight: 14,
  },
  overflowText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    lineHeight: 14,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
});
